import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import EnhancedDropdown from "./EnhancedDropdown";

/**
 * Enhanced InputField Component
 * Modern, beautiful input with smooth animations and better UX
 */
export const EnhancedInputField = ({
  label,
  type = "text",
  name,
  placeholder,
  value,
  options = [],
  handleChange,
  required = false,
  disabled = false,
  error = "",
  className = "",
  // Pagination props for scroll-to-load
  onLoadMore = null,
  hasMore = false,
  loading = false,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const inputRef = useRef(null);

  // Close other dropdowns when one is opened
  const handleDropdownToggle = (dropdownId) => {
    setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);
  };

  const hasValue = value && value.toString().length > 0;
  const showLabelFloat = isFocused || hasValue;
  // Show placeholder when there's no value (always visible when empty)
  const showPlaceholder = !hasValue && placeholder;

  return (
    <div className={`relative ${className}`}>
      {/* Label - Always show, floats when focused or has value */}
      <motion.label
        htmlFor={name}
        className={`absolute left-3 transition-all duration-200 pointer-events-none z-10 ${showLabelFloat
          ? "top-2 text-xs text-blue-600 dark:text-blue-400 font-medium"
          : "top-3.5 text-sm text-gray-500 dark:text-gray-400 opacity-0"
          } ${error ? "text-red-600 dark:text-red-400" : ""}`}
        animate={{
          y: showLabelFloat ? -8 : 0,
          scale: showLabelFloat ? 0.85 : 1,
          opacity: showLabelFloat ? 1 : 0,
        }}
        transition={{ duration: 0.2 }}
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </motion.label>

      {type === "select" ? (
        <div className="relative">
          {/* Label for dropdown */}
          <motion.label
            htmlFor={name}
            className={`absolute left-3 transition-all duration-200 pointer-events-none z-10 ${showLabelFloat
              ? "top-2 text-xs text-blue-600 dark:text-blue-400 font-medium"
              : "top-3.5 text-sm text-gray-500 dark:text-gray-400 opacity-0"
              } ${error ? "text-red-600 dark:text-red-400" : ""}`}
            animate={{
              y: showLabelFloat ? -8 : 0,
              scale: showLabelFloat ? 0.85 : 1,
              opacity: showLabelFloat ? 1 : 0,
            }}
            transition={{ duration: 0.2 }}
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </motion.label>

          <EnhancedDropdown
            label={label}
            title={placeholder || label}
            items={options}
            onItemSelect={(selectedItem) => {
              handleChange({
                target: {
                  name: name,
                  value: selectedItem,
                },
              });
            }}
            disabled={disabled}
            id={name}
            isOpen={activeDropdown === name}
            onToggle={handleDropdownToggle}
            selectedValue={value}
            error={error}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            loading={loading}
            placeholder={placeholder}
          />
        </div>
      ) : (
        <div className="relative">
          <motion.input
            ref={inputRef}
            type={type}
            name={name}
            id={name}
            placeholder={showPlaceholder ? placeholder : ""}
            value={value || ""}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            required={required}
            disabled={disabled}
            aria-invalid={!!error}
            aria-describedby={error ? `${name}-error` : undefined}
            className={`
              w-full px-3
              ${showLabelFloat ? "pt-6 pb-2.5" : "py-3.5"}
              text-sm text-gray-900 dark:text-white 
              placeholder:text-gray-400 dark:placeholder:text-gray-500
              placeholder:opacity-100
              bg-white dark:bg-gray-800
              border-2 rounded-lg
              transition-all duration-200
              focus:outline-none focus:ring-0
              disabled:bg-gray-50 dark:disabled:bg-gray-900
              disabled:cursor-not-allowed
              disabled:opacity-60
              ${error
                ? "border-red-500 focus:border-red-600 dark:border-red-500 dark:focus:border-red-600"
                : isFocused
                  ? "border-blue-500 focus:border-blue-600 dark:border-blue-500 dark:focus:border-blue-600"
                  : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
              }
            `}
            whileFocus={{ scale: 1.01 }}
            transition={{ duration: 0.2 }}
            {...rest}
          />

          {/* Focus indicator line */}
          {isFocused && !error && (
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-b-lg"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              exit={{ scaleX: 0 }}
              transition={{ duration: 0.3 }}
            />
          )}

          {/* Error indicator line */}
          {error && (
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-500 rounded-b-lg"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.3 }}
            />
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <motion.p
          id={`${name}-error`}
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1.5 text-xs text-red-600 dark:text-red-400 flex items-center gap-1"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </motion.p>
      )}
    </div>
  );
};

export default EnhancedInputField;
